import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Image, Store, Bot, GitCompare, MessageCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import AIChatbot from "@/components/AIChatbot";

interface MenuDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MenuDrawer = ({ open, onOpenChange }: MenuDrawerProps) => {
  const navigate = useNavigate();
  const [showAIChat, setShowAIChat] = useState(false);

  const handleClose = () => {
    onOpenChange(false);
    setShowAIChat(false);
  };

  const handleNavigate = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <Drawer 
      open={open} 
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) setShowAIChat(false);
      }} 
      shouldScaleBackground={false}
    >
      <DrawerContent className="h-full bg-background/[0.78] backdrop-blur-lg border-none">
        <AnimatePresence mode="wait">
          {showAIChat ? (
            <AIChatbot key="chatbot" onBack={() => setShowAIChat(false)} />
          ) : (
            <motion.div
              key="menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col h-full"
            >
              <DrawerHeader className="relative flex-shrink-0">
                <button 
                  onClick={handleClose}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90"
                >
                  <X className="w-6 h-6 text-foreground" />
                </button>
                <DrawerTitle className="text-2xl font-bold text-center">Menu</DrawerTitle>
              </DrawerHeader>
              
              <ScrollArea className="flex-1 px-4">
                <div className="flex flex-col items-center gap-4 py-8 pb-24">
                  {/* Sfondi */}
                  <motion.button 
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90 group"
                    onClick={() => handleNavigate("/sfondi")}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <Image className="w-12 h-12 text-foreground transition-all duration-300 group-hover:rotate-[-8deg] group-active:rotate-0" />
                    <span className="text-lg font-medium">Sfondi</span>
                  </motion.button>

                  {/* Apple Store */}
                  <motion.button 
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90 group"
                    onClick={() => handleNavigate("/apple-store")}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Store className="w-12 h-12 text-foreground transition-all duration-300 group-hover:rotate-[-8deg] group-active:rotate-0" />
                    <span className="text-lg font-medium">Apple Store</span>
                  </motion.button>

                  {/* Tecly AI */}
                  <motion.button 
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90 group"
                    onClick={() => setShowAIChat(true)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <Bot className="w-12 h-12 text-primary transition-all duration-300 group-hover:rotate-[-8deg] group-active:rotate-0" />
                    <span className="text-lg font-medium bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Tecly AI</span>
                  </motion.button>

                  {/* Messaggi */}
                  <motion.button 
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90 group"
                    onClick={() => handleNavigate("/messaggi")}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <MessageCircle className="w-12 h-12 text-green-500 transition-all duration-300 group-hover:rotate-[-8deg] group-active:rotate-0" />
                    <span className="text-lg font-medium text-green-500">Messaggi</span>
                  </motion.button>

                  {/* Confronta iPhone */}
                  <motion.button 
                    className="flex flex-col items-center gap-2 p-5 rounded-2xl hover:bg-muted/30 transition-all duration-300 hover:scale-110 active:scale-90 group"
                    onClick={() => handleNavigate("/confronta-iphone")}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <GitCompare className="w-12 h-12 text-teal-500 transition-all duration-300 group-hover:rotate-[-8deg] group-active:rotate-0" />
                    <span className="text-lg font-medium text-teal-500">Confronta iPhone</span>
                  </motion.button>
                </div>
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>
      </DrawerContent>
    </Drawer>
  );
};
